<template>
  <v-container
    class="px-10 my-3"
    fluid
  >
    <portal to="page-title">
      <v-breadcrumbs :items="[{ text: $tc('labels.general', 2), disabled: false }, { text: $t('labels.eventTypes'), disabled: false }]">
        <template v-slot:divider>
          <v-icon size="8">
            far fa-chevron-right
          </v-icon>
        </template>
      </v-breadcrumbs>
    </portal>
    <v-card
      id="eventTypes"
    >
      <v-row
        class="actions"
        no-gutters
      >
        <v-col>
          <v-text-field
            v-model.trim="filters.name"
            :append-icon="filters.name ? '' : 'fal fa-search'"
            class="name-filter pt-3 pb-1 ml-4 extra-dense-text-field d-inline-block"
            :clearable="!!filters.name"
            dense
            hide-details
            :placeholder="$t('labels.searchByName')"
            solo
          />
          <v-btn
            class="float-right mt-2 mr-4"
            color="secondary"
            @click="openEventTypeDialog()"
          >
            {{ $t('labels.addEventType') }}
          </v-btn>
        </v-col>
      </v-row>
      <v-data-table
        v-resize.quiet="onWindowResized"
        fixed-header
        :headers="headers"
        :header-props="{ sortIcon: 'fa fa-arrow-up' }"
        hide-default-footer
        :items="paginatedEventTypes"
        :items-per-page="pagination.itemsPerPage"
        must-sort
        single-select
        :sort-by.sync="sort.by"
        :sort-desc.sync="sort.desc"
      >
        <template #header.description="{ header }">
          <span class="grey--text text--darken-3">
            {{ header.text }}
          </span>
        </template>
        <template #item="{ item, headers: tableHeaders }">
          <tr
            :class="item.obsolete ? 'obsolete' : ''"
            @mouseenter="hoveredItem = item"
            @mouseleave="hoveredItem = null"
          >
            <td
              v-for="header in tableHeaders"
              :key="header.value"
              class="text-start"
            >
              <span
                v-if="header.value === 'name'"
                class="grey--text text--darken-3 text-truncate d-block"
                :style="{ width: `${header.width}px` }"
                :title="item.name"
              >
                {{ item.name }}
              </span>
              <span
                v-if="header.value === 'description'"
                class="grey--text text--darken-3 text-truncate d-block"
                :style="{ width: `${header.width}px` }"
                :title="item.description"
              >
                {{ item.description }}
              </span>
              <template v-if="header.value === 'adminOnly'">
                <span
                  class="d-inline-block"
                  :style="{ 'min-width': '95px' }"
                >
                  <v-icon
                    v-if="item.adminOnly"
                    color="success"
                    small
                  >
                    fas fa-check-circle
                  </v-icon>
                  <v-icon
                    v-else
                    color="grey"
                    small
                  >
                    far fa-times-circle
                  </v-icon>
                </span>
              </template>
              <template v-if="header.value === 'requestOnly'">
                <span
                  class="d-inline-block"
                  :style="{ 'min-width': '95px' }"
                >
                  <v-icon
                    v-if="item.requestOnly"
                    color="success"
                    small
                  >
                    fas fa-check-circle
                  </v-icon>
                  <v-icon
                    v-else
                    color="grey"
                    small
                  >
                    far fa-times-circle
                  </v-icon>
                </span>
              </template>
              <template v-if="header.value === 'visibleToOtherStaff'">
                <span
                  class="d-inline-block"
                  :style="{ 'min-width': '95px' }"
                >
                  <v-icon
                    v-if="item.visibleToOtherStaff"
                    color="success"
                    small
                  >
                    fas fa-check-circle
                  </v-icon>
                  <v-icon
                    v-else
                    color="grey"
                    small
                  >
                    far fa-times-circle
                  </v-icon>
                </span>
              </template>
            </td>
            <v-speed-dial
              absolute
              class="mt-6 event-type-dial"
              :value="hoveredItem && hoveredItem.id === item.id"
              right
              :direction="'left'"
              :transition="'slide-x-reverse-transition'"
            >
              <v-btn
                class="edit"
                fab
                height="30"
                width="30"
                :title="$t('labels.editEventType')"
                @click="openEventTypeDialog(item)"
              >
                <v-icon small>
                  fal fa-pencil
                </v-icon>
              </v-btn>
              <v-btn
                class="delete"
                fab
                height="30"
                width="30"
                :title="$t('labels.deleteEventType')"
                @click="openDeleteConfirmationDialog(item)"
              >
                <v-icon
                  color="error"
                  small
                >
                  fal fa-trash-alt
                </v-icon>
              </v-btn>
            </v-speed-dial>
          </tr>
        </template>
        <template #footer>
          <v-pagination
            v-model="pagination.currentPage"
            class="py-4 footer"
            color="secondary"
            :length="numberOfPages"
            next-icon="far fa-chevron-right"
            prev-icon="far fa-chevron-left"
            :total-visible="pagination.maxPaginationControls"
          />
        </template>
      </v-data-table>
    </v-card>
    <v-dialog
      v-if="showEventTypeDialog"
      persistent
      width="500px"
      :value="showEventTypeDialog"
    >
      <v-card
        id="eventType"
        class="pa-0"
      >
        <v-card-title class="body-2 d-block mb-2">
          <span class="body-2 font-weight-medium">
            {{ selectedEventType.id ? $t('labels.editEventType') : $t('labels.addEventType') }}
            {{ selectedEventType.name ? ` - ${selectedEventType.name}`: '' }}
          </span>
          <v-btn
            class="float-right mt-n1"
            :disabled="saving"
            icon
            small
            @click="closeEventTypeDialog"
          >
            <v-icon small>
              fal fa-times
            </v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="px-0">
          <ValidationObserver
            v-slot="{ invalid, passes }"
          >
            <v-form class="event-type">
              <v-container class="py-0 px-6">
                <v-row dense>
                  <v-col
                    cols="6"
                  >
                    <VeeTextField
                      v-model.trim="selectedEventType.name"
                      dense
                      :label="$t('labels.name')"
                      name="name"
                      outlined
                      :rules="{ required: true, max: 32, excluded: namesInUse }"
                    />
                  </v-col>
                  <v-col
                    cols="6"
                  >
                    <VeeTextField
                      v-model.trim="selectedEventType.description"
                      dense
                      :label="$t('labels.description')"
                      name="description"
                      outlined
                      rules="max:256"
                    />
                  </v-col>
                </v-row>
                <v-row dense>
                  <v-col cols="12">
                    <v-switch
                      v-model="selectedEventType.adminOnly"
                      class="pt-0 mt-1 d-inline-block admin-only"
                      color="success"
                      dense
                      inset
                      hide-details
                      @change="onAdminOnlyChange"
                    >
                      <template v-slot:label>
                        <span
                          class="mr-4 body-2 grey--text text--darken-3"
                          :title="$t('labels.hospitalOnly')"
                        >
                          {{ $t('labels.hospitalOnly') }}
                        </span>
                      </template>
                    </v-switch>
                    <v-tooltip
                      max-width="300px"
                      right
                    >
                      <template #activator="{ on, attrs }">
                        <v-icon
                          class="mt-n1"
                          color="info"
                          small
                          v-bind="attrs"
                          v-on="on"
                        >
                          fal fa-question-circle
                        </v-icon>
                      </template>
                      <span class="body-2">
                        {{ $t('descriptions.hospitalOnlyEvent') }}
                      </span>
                    </v-tooltip>
                  </v-col>
                </v-row>
                <v-row
                  dense
                >
                  <v-col cols="12">
                    <v-switch
                      v-model="selectedEventType.requestOnly"
                      class="pt-0 mt-1 d-inline-block request-only"
                      color="success"
                      dense
                      :disabled="selectedEventType.adminOnly"
                      hide-details
                      inset
                    >
                      <template v-slot:label>
                        <span
                          class="mr-4 body-2 grey--text text--darken-3"
                          :title="$t('labels.requestOnly')"
                        >
                          {{ $t('labels.requestOnly') }}
                        </span>
                      </template>
                    </v-switch>
                    <v-tooltip
                      max-width="300px"
                      right
                    >
                      <template #activator="{ on, attrs }">
                        <v-icon
                          class="mt-n1"
                          color="info"
                          small
                          v-bind="attrs"
                          v-on="on"
                        >
                          fal fa-question-circle
                        </v-icon>
                      </template>
                      <span class="body-2">
                        {{ $t('descriptions.requestOnlyEvent') }}
                      </span>
                    </v-tooltip>
                  </v-col>
                </v-row>
                <v-row
                  class="mb-4"
                  dense
                >
                  <v-col
                    cols="12"
                  >
                    <v-switch
                      v-model="selectedEventType.visibleToOtherStaff"
                      class="pt-0 mt-1 d-inline-block visible-to-others"
                      color="success"
                      dense
                      hide-details
                      inset
                    >
                      <template v-slot:label>
                        <span
                          class="mr-4 body-2 grey--text text--darken-3"
                          :title="$t('labels.eventVisibleToOthers')"
                        >
                          {{ $t('labels.eventVisibleToOthers') }}
                        </span>
                      </template>
                    </v-switch>
                  </v-col>
                </v-row>
              </v-container>
              <!-- <v-row
                v-if="selectedEventType.id"
                class="grey lighten-4 mb-0 py-4 px-6"
                dense
              >
                <v-col>
                  <div class="grey--text text--darken-2 body-2 mb-3">
                    {{ $t('descriptions.obsolete', { type: $t('labels.eventType').toLowerCase() }) }}
                  </div>
                  <v-switch
                    v-model="selectedEventType.obsolete"
                    class="pt-0 mt-1 d-inline-block obsolete"
                    color="success"
                    dense
                    hide-details
                    inset
                  >
                    <template v-slot:label>
                      <span
                        class="mr-4 body-2 grey--text text--darken-3"
                        :title="$t('labels.obsolete')"
                      >
                        {{ $t('labels.obsolete') }}
                      </span>
                    </template>
                  </v-switch>
                </v-col>
              </v-row> -->
              <v-divider />
              <v-row dense>
                <v-col
                  class="text-right pb-0 pt-4 px-8"
                  cols="12"
                >
                  <v-btn
                    class="mr-4"
                    :disabled="saving"
                    text
                    @click="closeEventTypeDialog"
                  >
                    {{ $t('labels.cancel') }}
                  </v-btn>
                  <v-btn
                    v-if="selectedEventType.id"
                    class="submit"
                    color="secondary"
                    :disabled="invalid || saving"
                    @click.prevent="passes(update)"
                  >
                    <v-progress-circular
                      v-if="saving"
                      color="primary lighten-2"
                      indeterminate
                      size="22"
                      width="2"
                    />
                    <span v-else>
                      {{ $t('labels.save') }}
                    </span>
                  </v-btn>
                  <v-btn
                    v-else
                    class="submit"
                    color="secondary"
                    :disabled="invalid || saving"
                    @click.prevent="passes(add)"
                  >
                    <v-progress-circular
                      v-if="saving"
                      color="primary lighten-2"
                      indeterminate
                      size="22"
                      width="2"
                    />
                    <span v-else>
                      {{ $t('labels.add') }}
                    </span>
                  </v-btn>
                </v-col>
                <v-spacer />
              </v-row>
            </v-form>
          </ValidationObserver>
        </v-card-text>
      </v-card>
    </v-dialog>
    <DeleteConfirmation
      v-if="deleteDialog.show"
      action="org/checkEventTypeDependencies"
      :delete-body="$t('descriptions.continueDeleteEventType', { name: deleteDialog.eventType.name })"
      :dependencies-body="$t('descriptions.hasDependenciesEventType', { name: deleteDialog.eventType.name })"
      :item="deleteDialog.eventType"
      :title="$t('labels.deleteEventType?')"
      @close="closeDeleteConfirmationDialog"
      @del="deleteEventType"
    />
  </v-container>
</template>

<script>
import _ from 'lodash';
import VeeTextField from '@/components/form_controls/VeeTextField';
import DeleteConfirmation from '@/components/admin/DeleteConfirmation';
import { showStatus } from '@/plugins/vue-notification';
import { ERROR_CODES } from '@/services/constants';

export default {
  components: {
    DeleteConfirmation,
    VeeTextField
  },
  data () {
    return {
      deleteDialog: {
        show: false,
        eventType: null
      },
      filters: {
        name: ''
      },
      hoveredItem: null,
      pagination: {
        currentPage: 1,
        itemsPerPage: 10,
        maxPaginationControls: 7
      },
      saving: false,
      selectedEventType: null,
      showEventTypeDialog: false,
      sort: {
        by: ['name'],
        desc: [false]
      }
    };
  },
  computed: {
    endTimes () {
      if (!this.selectedEventType) {
        return [];
      }
      const times = this.getTimes();
      const index = _.findIndex(times, (t) => t.id === this.selectedEventType.startTime);
      if (index >= 0) {
        times[index].disabled = true;
      }
      return times;
    },
    filteredEventTypes () {
      const filters = [];

      if (this.filters.name) {
        filters.push((eventType) => eventType.name.toLowerCase().indexOf(this.filters.name.toLowerCase()) >= 0);
      }
      const orders = this.sort.desc.map((desc) => desc ? 'desc' : 'asc');
      let eventTypes = this.eventTypes;
      if (filters.length > 0) {
        eventTypes = _.filter(this.eventTypes, (eventType) => filters.reduce((matches, filter) => {
          matches &= filter(eventType);
          return matches;
        }, true));
      }

      return _.orderBy(eventTypes, this.sort.by, orders);
    },
    headers () {
      return [
        {
          sortable: true,
          text: this.$t('labels.name'),
          value: 'name',
          width: 200
        },
        {
          sortable: false,
          text: this.$t('labels.description'),
          value: 'description',
          width: 250
        },
        {
          sortable: true,
          text: this.$t('labels.hospitalOnly'),
          value: 'adminOnly',
          width: '*'
        },
        {
          sortable: true,
          text: this.$t('labels.requestOnly'),
          value: 'requestOnly',
          width: '*'
        },
        {
          sortable: true,
          text: this.$t('labels.eventVisibleToOthers'),
          value: 'visibleToOtherStaff',
          width: '*'
        }
      ];
    },
    namesInUse () {
      let eventTypes = this.eventTypes;
      if (this.selectedEventType) {
        eventTypes = _.filter(this.eventTypes, (st) => st.id !== this.selectedEventType.id);
      }
      return eventTypes.map((st) => st.name);
    },
    numberOfPages () {
      if (!this.pagination.itemsPerPage) {
        return null;
      }
      return Math.ceil(this.filteredEventTypes.length / this.pagination.itemsPerPage);
    },
    paginatedEventTypes () {
      if (this.pagination.itemsPerPage) {
        const start = (this.pagination.currentPage - 1) * this.pagination.itemsPerPage;
        return this.filteredEventTypes.slice(start, start + this.pagination.itemsPerPage);
      }
      return this.filteredEventTypes;
    },
    eventTypes () {
      return _.cloneDeep(this.$store.state.org.eventTypes);
    },
    shortcutsInUse () {
      let eventTypes = this.eventTypes;
      if (this.selectedEventType) {
        eventTypes = _.filter(this.eventTypes, (st) => st.id !== this.selectedEventType.id);
      }
      eventTypes = _.filter(eventTypes, (st) => !!st.styles.web.keyboardShortcut);
      return eventTypes.map((st) => st.styles.web.keyboardShortcut);
    },
    startTimes () {
      if (!this.selectedEventType) {
        return [];
      }
      const times = this.getTimes();
      const index = _.findIndex(times, (t) => t.id === this.selectedEventType.endTime);
      if (index >= 0) {
        times[index].disabled = true;
      }
      return times;
    }
  },
  mounted () {
    this.calcPageSize();
  },
  methods: {
    add () {
      if (!this.saving) {
        this.saving = true;
        this.dispatch('org/createEventType', this.selectedEventType).then(() => {
          showStatus({
            text: this.$t('descriptions.eventTypeSaveSuccess'),
            type: 'success'
          });
          this.closeEventTypeDialog();
        }).catch(error => {
          const data = {
            error: _.get(error, 'response.data')
          };

          showStatus({
            text: this.$t('descriptions.eventTypeSaveFail'),
            type: 'error',
            data
          });
        }).finally(() => {
          this.saving = false;
        });
      }
    },
    calcPageSize () {
      const topNavHeight = 48;
      const tabBarHeight = 55;
      const tableHeaderHeight = 36;
      const tdHeight = 60;
      const tableFooterHeight = 35;
      const marginAndPadding = 40;
      const tbodyHeight = window.innerHeight - (
        topNavHeight + tabBarHeight + tableHeaderHeight + tableFooterHeight + marginAndPadding
      );

      this.pagination.itemsPerPage = Math.floor(tbodyHeight / tdHeight);
    },
    closeDeleteConfirmationDialog () {
      this.deleteDialog.show = false;
      this.deleteDialog.eventType = null;
    },
    closeEventTypeDialog () {
      this.showEventTypeDialog = false;
      this.selectedEventType = null;
    },
    deleteEventType (eventType) {
      this.dispatch('org/deleteEventType', eventType.id).then(() => {
        showStatus({
          text: this.$t('descriptions.eventTypeDeletionSuccess', { name: eventType.name }),
          type: 'success'
        });
        this.$emit('close');
      }).catch((error) => {
        const status = _.get(error, 'response.status', '');
        const responseData = {
          error: _.get(error, 'response.data')
        };

        let text = '';
        if (status === ERROR_CODES.http412PreconditionFailed) {
          text = this.$t('descriptions.eventTypeCannotDelete', { name: eventType.name });
        } else {
          text = this.$t('descriptions.eventTypeDeletionFail', { name: eventType.name });
        }

        showStatus({
          text,
          type: 'error',
          responseData
        });
      }).finally(() => {
        this.closeDeleteConfirmationDialog();
      });
    },
    // This function is added mainly for easy of mocking during in unit tests.
    dispatch (action, payload) {
      return new Promise((resolve, reject) => {
        this.$store.dispatch(action, payload).then(response => {
          resolve(response);
        }).catch(error => {
          reject(error);
        });
      });
    },
    onAdminOnlyChange () {
      if (this.selectedEventType.adminOnly) {
        this.selectedEventType.requestOnly = false;
      }
    },
    onWindowResized () {
      this.calcPageSize();
    },
    openDeleteConfirmationDialog (eventType) {
      this.deleteDialog.eventType = eventType;
      this.deleteDialog.show = true;
    },
    openEventTypeDialog (eventType) {
      let selectedEventType = {
        adminOnly: false,
        description: '',
        name: '',
        obsolete: false,
        requestOnly: false,
        settings: {
          request: {
            disable: []
          }
        },
        styles: {
          mobile: { bgColor: '#D9CAFD', color: '', symbolType: 'bar' },
          web: { bgColor: '', color: '#D9CAFD', keyboardShortcut: '', symbolType: 'bar' }
        },
        visibleToOtherStaff: false
      };
      if (eventType) {
        selectedEventType = _.cloneDeep(eventType);
      }
      this.selectedEventType = selectedEventType;
      this.showEventTypeDialog = true;
    },
    update () {
      if (!this.saving) {
        this.saving = true;
        this.dispatch('org/updateEventType', this.selectedEventType).then(() => {
          showStatus({
            text: this.$t('descriptions.eventTypeSaveSuccess'),
            type: 'success'
          });
          this.closeEventTypeDialog();
        }).catch(error => {
          const data = {
            error: _.get(error, 'response.data')
          };

          showStatus({
            text: this.$t('descriptions.eventTypeSaveFail'),
            type: 'error',
            data
          });
        }).finally(() => {
          this.saving = false;
        });
      }
    }
  }
};
</script>

<style lang="scss">
#eventTypes {
  .actions {
    background-color: white;
    border-bottom: 1px solid map-get($grey, 'lighten-2');
  }
  .footer {
    border-top: thin solid rgba(0, 0, 0, 0.12);
  }
  .event-type-dial {
    right: 0px !important;
  }
  thead th span {
    font-size: 12px !important;
  }
  tr.obsolete td span {
    color: map-get($grey, 'base') !important;
  }
}
</style>
